import { z } from "zod"

import { commandIdSchema, entityIdSchema } from "./identifiers.js"

const instantSchema = z.iso.datetime({ offset: true })
const boundedNoteSchema = z.string().trim().min(1).max(2_000)

export const visitChecklistItemV1Schema = z.object({
  itemId: entityIdSchema,
  label: z.string().trim().min(1).max(160),
  kind: z.enum(["Safety", "Service", "Evidence"]),
  required: z.boolean(),
  evidenceRequirement: z.enum(["None", "Photo", "Document", "Any Media"]),
}).strict()

export const visitExecutionPlanConfigureV1Schema = z.object({
  visitId: entityIdSchema,
  visitVersion: z.number().int().positive(),
  planVersion: z.number().int().positive(),
  checklistItems: z.array(visitChecklistItemV1Schema).max(25),
  requiresCustomerAssetObservation: z.boolean(),
  requiresMaterialEntry: z.boolean(),
  inventoryLocationIds: z.array(entityIdSchema).max(10),
}).strict().superRefine((value, context) => {
  if (
    new Set(value.checklistItems.map((item) => item.itemId)).size !==
    value.checklistItems.length
  ) {
    context.addIssue({
      code: "custom",
      message: "Checklist items must be unique.",
      path: ["checklistItems"],
    })
  }
  if (
    new Set(value.inventoryLocationIds).size !==
    value.inventoryLocationIds.length
  ) {
    context.addIssue({
      code: "custom",
      message: "Inventory Locations must be unique.",
      path: ["inventoryLocationIds"],
    })
  }
})

export const visitExecutionPlanProjectionV1Schema =
  visitExecutionPlanConfigureV1Schema.extend({
    executionState: z.enum(["Ready", "En Route", "Arrived", "In Progress"]),
    recordVersion: z.number().int().positive(),
  }).strict()

export const visitExecutionAdvanceV1Schema = z.object({
  visitId: entityIdSchema,
  visitVersion: z.number().int().positive(),
  planRecordVersion: z.number().int().positive(),
  targetState: z.enum(["En Route", "Arrived", "In Progress"]),
  capturedAt: instantSchema,
  exceptionReason: boundedNoteSchema.nullable(),
}).strict()

export const visitSkipV1Schema = z.object({
  skipId: entityIdSchema,
  visitId: entityIdSchema,
  visitVersion: z.number().int().positive(),
  planRecordVersion: z.number().int().positive(),
  reason: boundedNoteSchema.nullable(),
  capturedAt: instantSchema,
}).strict()

export const visitSkipReasonUpdateV1Schema = z.object({
  skipId: entityIdSchema,
  visitId: entityIdSchema,
  visitVersion: z.number().int().positive(),
  planRecordVersion: z.number().int().positive(),
  reason: boundedNoteSchema,
  capturedAt: instantSchema,
}).strict()

export const visitSkipReopenV1Schema = z.object({
  skipId: entityIdSchema,
  visitId: entityIdSchema,
  visitVersion: z.number().int().positive(),
  planRecordVersion: z.number().int().positive(),
  capturedAt: instantSchema,
}).strict()

export const visitChecklistRecordV1Schema = z.object({
  responseId: entityIdSchema,
  visitId: entityIdSchema,
  visitVersion: z.number().int().positive(),
  planVersion: z.number().int().positive(),
  planRecordVersion: z.number().int().positive(),
  itemId: entityIdSchema,
  outcome: z.enum(["Completed", "Exception", "Not Applicable"]),
  note: boundedNoteSchema.nullable(),
  evidenceMediaIds: z.array(entityIdSchema).max(10),
  capturedAt: instantSchema,
}).strict().superRefine((value, context) => {
  if (value.outcome !== "Completed" && value.note === null) {
    context.addIssue({
      code: "custom",
      message: "An exception or not-applicable result requires a reason.",
      path: ["note"],
    })
  }
})

export const visitCustomerAssetObservationV1Schema = z.object({
  observationId: entityIdSchema,
  visitId: entityIdSchema,
  visitVersion: z.number().int().positive(),
  planRecordVersion: z.number().int().positive(),
  customerAssetId: entityIdSchema.nullable(),
  label: z.string().trim().min(1).max(160),
  condition: z.enum(["Normal", "Needs Attention", "Unsafe"]),
  reading: z.string().trim().min(1).max(120).nullable(),
  notes: boundedNoteSchema.nullable(),
  capturedAt: instantSchema,
}).strict().superRefine((value, context) => {
  if (value.condition !== "Normal" && value.notes === null) {
    context.addIssue({
      code: "custom",
      message: "A non-normal customer asset requires a note.",
      path: ["notes"],
    })
  }
})

const positiveDecimalSchema = z.string().regex(
  /^(?:0*[1-9]\d{0,11})(?:\.\d{1,6})?$/,
)

export const visitMaterialUseProvisionalV1Schema = z.object({
  materialUseId: entityIdSchema,
  visitId: entityIdSchema,
  visitVersion: z.number().int().positive(),
  planRecordVersion: z.number().int().positive(),
  inventoryLocationId: entityIdSchema,
  itemCode: z.string().trim().min(1).max(140),
  quantity: positiveDecimalSchema,
  unitOfMeasure: z.string().trim().min(1).max(40),
  capturedAt: instantSchema,
  notes: boundedNoteSchema.nullable(),
}).strict()

export const visitMaterialUseFieldProgressV1Schema = z.object({
  progressVersion: z.literal(1),
  commandId: commandIdSchema,
  materialUseId: entityIdSchema,
  visitId: entityIdSchema,
  inventoryLocationId: entityIdSchema,
  itemCode: z.string().trim().min(1).max(140),
  quantity: positiveDecimalSchema,
  unitOfMeasure: z.string().trim().min(1).max(40),
  capturedAt: instantSchema,
  state: z.literal("Queued Provisional"),
  stockPosted: z.literal(false),
  authoritativeQuantityChanged: z.literal(false),
  authoritativeVisitCostChanged: z.literal(false),
  syncRequired: z.literal(true),
}).strict()

export const visitExecutionSummaryV1Schema = z.object({
  completedItemIds: z.array(entityIdSchema).max(25),
  exceptionItemIds: z.array(entityIdSchema).max(25),
  notApplicableItemIds: z.array(entityIdSchema).max(25),
  customerAssetObservationCount: z.number().int().nonnegative().max(100),
  provisionalMaterialUseCount: z.number().int().nonnegative().max(100),
  reviewRequired: z.boolean(),
}).strict()

export type VisitChecklistRecordV1 = z.infer<
  typeof visitChecklistRecordV1Schema
>
export type VisitCustomerAssetObservationV1 = z.infer<
  typeof visitCustomerAssetObservationV1Schema
>
export type VisitExecutionAdvanceV1 = z.infer<
  typeof visitExecutionAdvanceV1Schema
>
export type VisitSkipV1 = z.infer<typeof visitSkipV1Schema>
export type VisitSkipReasonUpdateV1 = z.infer<
  typeof visitSkipReasonUpdateV1Schema
>
export type VisitSkipReopenV1 = z.infer<typeof visitSkipReopenV1Schema>
export type VisitExecutionPlanConfigureV1 = z.infer<
  typeof visitExecutionPlanConfigureV1Schema
>
export type VisitExecutionPlanProjectionV1 = z.infer<
  typeof visitExecutionPlanProjectionV1Schema
>
export type VisitExecutionSummaryV1 = z.infer<
  typeof visitExecutionSummaryV1Schema
>
export type VisitMaterialUseProvisionalV1 = z.infer<
  typeof visitMaterialUseProvisionalV1Schema
>
export type VisitMaterialUseFieldProgressV1 = z.infer<
  typeof visitMaterialUseFieldProgressV1Schema
>
